import { useEffect, useState } from "react";
import { Plus, Loader2, X, Sparkles, Trash2, Pencil, Users, User, Globe, Lock, Archive, Newspaper, Megaphone } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle } from "@/components/Brutal";
import { PhotoInput, VoiceButton } from "@/components/AiComponents";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { useConfirm } from "@/components/ConfirmProvider";
import api from "@/lib/api";
import { toast } from "sonner";

const backend = process.env.REACT_APP_BACKEND_URL;
const srcOf = (u) => (u ? (u.startsWith("http") ? u : `${backend}${u}`) : null);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long" }); } catch { return ""; } };

const AUD = { everyone: { icon: Globe, label: "Iedereen" }, persons: { icon: User, label: "Personen" }, groups: { icon: Users, label: "Groepen" } };

function StoryDialog({ open, onClose, onSaved, editing, members, groups }) {
  const tr = useT();
  const [form, setForm] = useState({ title: "", text: "", image_url: null, important: false, audience: { type: "everyone", user_ids: [], group_ids: [] } });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (editing) setForm({ title: editing.title || "", text: editing.text || "", image_url: editing.image_url || null, important: !!editing.important, audience: { type: "everyone", user_ids: [], group_ids: [], ...(editing.audience || {}) } });
    else setForm({ title: "", text: "", image_url: null, important: false, audience: { type: "everyone", user_ids: [], group_ids: [] } });
  }, [editing, open]);

  if (!open) return null;
  const aud = form.audience;
  const toggleId = (key, id) => {
    const cur = aud[key] || [];
    setForm({ ...form, audience: { ...aud, [key]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] } });
  };

  const aiGen = async () => {
    if (!form.text.trim()) return toast.error(tr("Typ eerst even wat er is gebeurd 🙂"));
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/story", { input: form.text });
      setForm((f) => ({ ...f, text: data.text }));
      toast.success(tr("Opgepoetst met AI ✨"));
    } catch (e) { toast.error(e.response?.data?.detail || tr("AI-tekst mislukt")); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error(tr("Geef je bericht even een titel"));
    setBusy(true);
    try {
      const body = { ...form, title: form.title.trim() };
      if (editing) await api.put(`/stories/${editing.id}`, body);
      else await api.post("/stories", body);
      toast.success(editing ? tr("Bijgewerkt") : tr("Geplaatst! 🎉"));
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Opslaan mislukt")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="story-dialog">
      <div className="bg-brand-cream w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl brutal max-h-[92vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-head font-black text-xl">{editing ? tr("Bericht bewerken") : tr("Nieuw bericht")}</p>
          <button onClick={onClose} data-testid="story-dialog-close" className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><X className="w-5 h-5" strokeWidth={2.5} /></button>
        </div>
        <input data-testid="story-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={tr("Titel (bv. Nieuwe huisgenoot!)")} className="w-full brutal-sm rounded-xl px-4 py-3 font-bold outline-none" />
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-bold">{tr("Wat is er gebeurd?")}</span>
            <div className="flex gap-1.5">
              <VoiceButton onText={(t) => setForm((f) => ({ ...f, text: (f.text ? f.text + " " : "") + t }))} testid="story-voice" />
              <button type="button" data-testid="story-ai" onClick={aiGen} disabled={aiBusy} className="brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold bg-brand-violet text-white flex items-center gap-1">
                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />}{tr("AI-tekst")}
              </button>
            </div>
          </div>
          <textarea data-testid="story-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} placeholder={tr("Vertel het verhaal… of tik AI-tekst om het op te poetsen.")} className="w-full brutal-sm rounded-xl px-4 py-3 outline-none resize-none" />
        </div>
        <div>
          <p className="text-sm font-bold mb-1.5">{tr("Afbeelding")}</p>
          <PhotoInput value={form.image_url} onChange={(u) => setForm({ ...form, image_url: u })} aiPrompt={form.title} testid="story-photo" />
        </div>
        <div>
          <p className="text-sm font-bold mb-1.5">{tr("Wie mag dit zien?")}</p>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(AUD).map(([k, v]) => (
              <button key={k} data-testid={`story-aud-${k}`} onClick={() => setForm({ ...form, audience: { ...aud, type: k } })}
                className={`brutal-sm rounded-xl px-2 py-2.5 text-sm font-bold flex items-center justify-center gap-1.5 ${aud.type === k ? "bg-brand-violet text-white" : "bg-white"}`}>
                <v.icon className="w-4 h-4" strokeWidth={2.5} />{tr(v.label)}
              </button>
            ))}
          </div>
          {aud.type === "persons" && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="story-persons">
              {members.map((m) => (
                <button key={m.user_id} onClick={() => toggleId("user_ids", m.user_id)}
                  className={`brutal-sm rounded-full px-3 py-1.5 text-sm font-bold ${(aud.user_ids || []).includes(m.user_id) ? "bg-brand-green" : "bg-white"}`}>{m.name}</button>
              ))}
            </div>
          )}
          {aud.type === "groups" && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="story-groups">
              {groups.map((g) => (
                <button key={g.id} onClick={() => toggleId("group_ids", g.id)}
                  className={`brutal-sm rounded-full px-3 py-1.5 text-sm font-bold ${(aud.group_ids || []).includes(g.id) ? "bg-brand-green" : "bg-white"}`}>{g.name}</button>
              ))}
            </div>
          )}
        </div>
        <button type="button" data-testid="story-important" onClick={() => setForm({ ...form, important: !form.important })}
          className={`w-full flex items-center justify-between brutal-sm rounded-xl px-3 py-2.5 ${form.important ? "bg-brand-yellow" : "bg-white"}`}>
          <span className="flex items-center gap-2 text-sm font-bold"><Megaphone className="w-4 h-4" strokeWidth={2.5} />{tr("Belangrijk nieuws — stuur ook een e-mail")}</span>
          <span className={`w-11 h-6 rounded-full relative transition-colors ${form.important ? "bg-brand-violet" : "bg-brand-ink/20"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.important ? "left-5" : "left-0.5"}`} />
          </span>
        </button>
        <BrutalButton data-testid="story-save" onClick={save} disabled={busy} className="w-full">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null}{editing ? tr("Opslaan") : tr("Plaatsen")}
        </BrutalButton>
      </div>
    </div>
  );
}

function StoryView({ story, onClose }) {
  if (!story) return null;
  const img = srcOf(story.image_url);
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="story-view">
      <div className="bg-brand-cream w-full max-w-lg rounded-2xl brutal max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {img && <img src={img} alt="" className="w-full h-56 object-cover rounded-t-2xl" />}
        <div className="p-5 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-head font-black text-2xl leading-tight">{story.title}</h2>
            <button onClick={onClose} data-testid="story-view-close" className="brutal-sm brutal-press rounded-full p-1.5 bg-white shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button>
          </div>
          <p className="text-xs font-bold text-brand-ink/50">{story.author_name} · {fmtDate(story.created_at)}</p>
          <p className="whitespace-pre-wrap leading-relaxed">{story.text}</p>
        </div>
      </div>
    </div>
  );
}

export default function Nieuws() {
  const { user, moduleOn } = useAuth();
  const tr = useT();
  const confirm = useConfirm();
  const [access, setAccess] = useState(true);
  const [stories, setStories] = useState([]);
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [acc, list] = await Promise.all([
        api.get("/stories/access"),
        api.get(`/stories?archived=${archived}`),
      ]);
      setAccess(acc.data.access);
      setStories(list.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [archived]);
  useEffect(() => {
    api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  const del = async (s) => {
    if (!(await confirm({ title: tr("Bericht verwijderen?"), body: tr("Dit kan niet ongedaan worden gemaakt.") }))) return;
    try { await api.delete(`/stories/${s.id}`); toast.success(tr("Verwijderd")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || tr("Mislukt")); }
  };

  if (!moduleOn("nieuws")) return (
    <div className="text-center py-20" data-testid="nieuws-off"><Newspaper className="w-12 h-12 mx-auto text-brand-ink/30 mb-3" /><p className="font-head font-black text-lg">{tr("Nieuws staat uit")}</p><p className="text-sm text-brand-ink/60 font-semibold">{tr("Een beheerder kan dit aanzetten bij Instellingen.")}</p></div>
  );

  return (
    <div className="space-y-4" data-testid="nieuws-page">
      <div className="flex items-center justify-between">
        <SectionTitle sub={tr("Deel wat er speelt in huis 📣")}>{tr("Nieuws")}</SectionTitle>
        <div className="flex gap-2">
          <button data-testid="nieuws-archive-toggle" onClick={() => setArchived((a) => !a)} className={`brutal-sm brutal-press rounded-full px-3 py-2 text-sm font-bold flex items-center gap-1.5 ${archived ? "bg-brand-ink text-white" : "bg-white"}`}><Archive className="w-4 h-4" strokeWidth={2.5} />{archived ? tr("Archief") : tr("Actueel")}</button>
          {access && !archived && <BrutalButton data-testid="nieuws-new" size="sm" onClick={() => { setEditing(null); setDialog(true); }}><Plus className="w-4 h-4" strokeWidth={2.5} />{tr("Bericht")}</BrutalButton>}
        </div>
      </div>

      {!access && (
        <BrutalCard data-testid="nieuws-upsell" className="bg-brand-yellow">
          <div className="flex items-start gap-3">
            <Lock className="w-6 h-6 shrink-0 mt-0.5" strokeWidth={2.5} />
            <div>
              <p className="font-head font-black">{tr("Nieuws is een extra 🎁")}</p>
              <p className="text-sm font-semibold">{tr("Tijdens je proef mag je 'm gratis gebruiken. Daarna zit Nieuws erbij vanaf een abonnement van 6 maanden of langer. Upgrade en deel er meteen op los!")}</p>
            </div>
          </div>
        </BrutalCard>
      )}

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 mx-auto animate-spin text-brand-violet" /></div>
      ) : stories.length === 0 ? (
        <div className="text-center py-16" data-testid="nieuws-empty"><Newspaper className="w-12 h-12 mx-auto text-brand-ink/30 mb-3" /><p className="font-head font-bold">{archived ? tr("Nog niets gearchiveerd") : tr("Nog geen berichten")}</p>{!archived && access && <p className="text-sm text-brand-ink/60 font-semibold">{tr("Plaats het eerste bericht en houd iedereen op de hoogte!")}</p>}</div>
      ) : (
        <div className="space-y-3">
          {stories.map((s) => {
            const img = srcOf(s.image_url);
            const mine = s.created_by === user?.user_id;
            const AudIcon = (AUD[s.audience?.type] || AUD.everyone).icon;
            return (
              <BrutalCard key={s.id} data-testid={`story-card-${s.id}`} className="p-0 overflow-hidden">
                <button onClick={() => setViewing(s)} className="w-full text-left flex gap-3 items-stretch">
                  {img && <img src={img} alt="" className="w-24 h-24 object-cover shrink-0" />}
                  <div className="py-3 pr-2 flex-1 min-w-0">
                    <p className="font-head font-black leading-tight truncate">{s.title}</p>
                    <p className="text-sm text-brand-ink/70 line-clamp-2 mt-0.5">{s.text}</p>
                    <p className="text-[11px] font-bold text-brand-ink/40 mt-1 flex items-center gap-1"><AudIcon className="w-3 h-3" strokeWidth={2.5} />{s.author_name} · {fmtDate(s.created_at)}</p>
                  </div>
                </button>
                {mine && !archived && (
                  <div className="flex border-t-2 border-brand-ink">
                    <button data-testid={`story-edit-${s.id}`} onClick={() => { setEditing(s); setDialog(true); }} className="flex-1 py-2 font-bold text-sm flex items-center justify-center gap-1.5 border-r-2 border-brand-ink"><Pencil className="w-4 h-4" strokeWidth={2.5} />{tr("Bewerk")}</button>
                    <button data-testid={`story-del-${s.id}`} onClick={() => del(s)} className="flex-1 py-2 font-bold text-sm text-brand-pink flex items-center justify-center gap-1.5"><Trash2 className="w-4 h-4" strokeWidth={2.5} />{tr("Verwijder")}</button>
                  </div>
                )}
              </BrutalCard>
            );
          })}
        </div>
      )}

      <StoryDialog open={dialog} onClose={() => setDialog(false)} onSaved={load} editing={editing} members={members} groups={groups} />
      <StoryView story={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
